// Mercapoli - Gestión de Reviews del Comercio
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MerchantHeader from '../../components/merchant/MerchantHeader';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, Star, MessageSquare, Flag, Send, User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { comercio as comercioApi } from '../../lib/api';
import { toast } from 'sonner';


const MerchantReviews = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [showRespuestaModal, setShowRespuestaModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [respuesta, setRespuesta] = useState('');
  const [motivoReporte, setMotivoReporte] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [reviewsData, statsData] = await Promise.all([
        comercioApi.getReviews(),
        comercioApi.getEstadisticas()
      ]);
      setReviews(reviewsData);
      setEstadisticas(statsData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar reviews');
    }
    setLoading(false);
  };

  const handleResponder = async () => {
    if (!respuesta || respuesta.length < 10) {
      toast.error('La respuesta debe tener al menos 10 caracteres');
      return;
    }

    try {
      await comercioApi.responderReview(selectedReview.review_id, respuesta);
      toast.success('Respuesta enviada');
      setShowRespuestaModal(false);
      setRespuesta('');
      loadData();
    } catch (error) {
      toast.error('Error al enviar respuesta');
    }
  };

  const handleReportar = async () => {
    if (!motivoReporte || motivoReporte.length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres');
      return;
    }

    try {
      await comercioApi.reportarReview(selectedReview.review_id, motivoReporte);
      toast.success('Review reportado para moderación');
      setShowReportModal(false);
      setMotivoReporte('');
      loadData();
    } catch (error) {
      toast.error('Error al reportar review');
    }
  };

  const renderStars = (calificacion) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${star <= calificacion ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).toLocaleDateString('es-GT', { 
      day: '2-digit', month: 'short', year: 'numeric' 
    });
  };

  // Calcular distribución de estrellas
  const distribucion = [5, 4, 3, 2, 1].map(stars => ({
    stars,
    count: reviews.filter(r => r.calificacion === stars).length,
    percentage: reviews.length > 0 ? (reviews.filter(r => r.calificacion === stars).length / reviews.length) * 100 : 0
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header con Logout */}
      <MerchantHeader title="Reviews" />

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Summary Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Calificación general */}
              <div className="text-center">
                <p className="text-5xl font-bold text-brand-primary">
                  {estadisticas?.calificacion?.promedio?.toFixed(1) || '5.0'}
                </p>
                <div className="flex justify-center mt-2">
                  {renderStars(Math.round(estadisticas?.calificacion?.promedio || 5))}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {reviews.length} reviews
                </p>
              </div>

              {/* Distribución */}
              <div className="flex-1 space-y-2">
                {distribucion.map(({ stars, count, percentage }) => (
                  <div key={stars} className="flex items-center gap-2">
                    <span className="text-sm w-6">{stars}</span>
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-500 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground w-8">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reviews List */}
        {reviews.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Star className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No hay reviews aún</p>
              <p className="text-sm text-muted-foreground mt-2">
                Los clientes podrán dejar reviews después de recibir sus pedidos
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <motion.div
                key={review.review_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className={review.reportado ? 'border-red-300 bg-red-50/50' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-brand-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-brand-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{review.cliente_nombre}</p>
                          <div className="flex items-center gap-2">
                            {renderStars(review.calificacion)}
                            <span className="text-xs text-muted-foreground">
                              {formatFecha(review.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {review.reportado && (
                          <Badge variant="destructive" className="text-xs">Reportado</Badge>
                        )}
                      </div>
                    </div>

                    <p className="text-sm mb-4">{review.comentario}</p>

                    {/* Respuesta del comercio */}
                    {review.respuesta_comercio && (
                      <div className="bg-muted p-3 rounded-lg mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <MessageSquare className="h-4 w-4 text-brand-primary" />
                          <span className="text-sm font-medium">Tu respuesta</span>
                          {review.fecha_respuesta && (
                            <span className="text-xs text-muted-foreground">
                              {formatFecha(review.fecha_respuesta)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{review.respuesta_comercio}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      {!review.respuesta_comercio && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedReview(review);
                            setShowRespuestaModal(true);
                          }}
                        >
                          <MessageSquare className="h-4 w-4 mr-1" />
                          Responder
                        </Button>
                      )}
                      {!review.reportado && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500"
                          onClick={() => {
                            setSelectedReview(review);
                            setShowReportModal(true);
                          }}
                        >
                          <Flag className="h-4 w-4 mr-1" />
                          Reportar
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Modal Responder */}
      <Dialog open={showRespuestaModal} onOpenChange={setShowRespuestaModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Responder a {selectedReview?.cliente_nombre}</DialogTitle>
          </DialogHeader>
          
          {selectedReview && (
            <div className="space-y-4">
              <div className="bg-muted p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  {renderStars(selectedReview.calificacion)}
                </div>
                <p className="text-sm">{selectedReview.comentario}</p>
              </div>

              <Textarea
                value={respuesta}
                onChange={(e) => setRespuesta(e.target.value)}
                placeholder="Escribe tu respuesta..."
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Mínimo 10 caracteres. Tu respuesta será visible para todos los clientes.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRespuestaModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleResponder}>
              <Send className="h-4 w-4 mr-2" />
              Enviar Respuesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Reportar */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reportar Review</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Si consideras que este review es inapropiado, falso o viola nuestras políticas, 
              puedes reportarlo para que nuestro equipo lo revise.
            </p>

            <Textarea
              value={motivoReporte}
              onChange={(e) => setMotivoReporte(e.target.value)}
              placeholder="Explica el motivo del reporte..."
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              Mínimo 10 caracteres. El review seguirá visible hasta que nuestro equipo lo revise.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportModal(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleReportar}>
              <Flag className="h-4 w-4 mr-2" />
              Reportar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantReviews;
